import type { settingTypes } from '@ipfabric/utils';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type JuniperMistFormValues =
  BaseVendorFormValues<settingTypes.JuniperMistApiSettings>;

type Props = VendorDialogProps<
  JuniperMistFormValues,
  settingTypes.JuniperMistApiSettings,
  settingTypes.UpdateJuniperMistApiSettings
>;

const resetSecretAttributes = (
  values: JuniperMistFormValues,
): JuniperMistFormValues => {
  return {
    ...values,
    apiToken: '',
  };
};

const BareJuniperMistForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';

  const msg = useLocale();
  const formik = useFormik<JuniperMistFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const { required, baseUrlStartsWith } =
        msg.discoverySettings.vendorsApi.validations;
      const errors: FormikErrors<JuniperMistFormValues> = {};

      if (!values.baseUrl) {
        errors.baseUrl = required;
      } else if (!hasHttpOrHttps(values.baseUrl)) {
        errors.baseUrl = baseUrlStartsWith;
      }

      if (!editMode && !values.apiToken) {
        errors.apiToken = required;
      }

      if (!values.apiVersion) {
        errors.apiVersion = required;
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values: JuniperMistFormValues) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          apiToken:
            values.apiToken || props.initialValues.apiToken || undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<JuniperMistFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikSelectField
        formik={formik}
        label={msg.discoverySettings.vendorsApi.labels.apiVersion}
        name='apiVersion'
        nonClearable
        options={[{ label: 'v1', value: 'v1' }]}
      />
      <FormikVendorApiBaseUrlField formik={formik} />
      <FormikTextField
        formik={formik}
        label={msg.discoverySettings.vendorsApi.labels.apiToken}
        name='apiToken'
        type='text'
        placeholder={editMode ? '(unchanged)' : undefined}
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const JuniperMistForm = memo(BareJuniperMistForm);
